import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, RotateCcw, Check, ArrowLeft, Upload, QrCode, X, Loader2, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/tyretrack`;

const CameraCapture = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const qrScannerRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [cameraError, setCameraError] = useState(false);
  const [scanMode, setScanMode] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [eprelLoading, setEprelLoading] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const [duplicateTire, setDuplicateTire] = useState(null);
  const [pendingEprelData, setPendingEprelData] = useState(null);
  const [showSellDialog, setShowSellDialog] = useState(false);
  const [soldQuantity, setSoldQuantity] = useState(1);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: facingMode,
  };

  // Cleanup QR scanner on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  // Start scanner when scanMode becomes true and div is ready
  useEffect(() => {
    if (scanMode && scannerReady && !scanning) {
      initScanner();
    }
  }, [scanMode, scannerReady]);

  const captureFromScanner = () => {
    const video = document.querySelector("#qr-reader video");
    if (!video) return null;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || video.clientWidth;
    canvas.height = video.videoHeight || video.clientHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg");
  };

  const stopScanner = async () => {
    if (qrScannerRef.current) {
      try {
        await qrScannerRef.current.stop();
        qrScannerRef.current = null;
      } catch (e) {
        console.log("Scanner already stopped");
      }
    }
    setScanning(false);
  };

  const initScanner = async () => {
    // Make sure previous scanner is stopped
    await stopScanner();
    
    // Small delay to ensure DOM is ready
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const qrReaderElement = document.getElementById("qr-reader");
    if (!qrReaderElement) {
      console.error("QR reader element not found");
      toast.error("Errore: elemento scanner non trovato");
      return;
    }

    try {
      setScanning(true);
      const html5QrCode = new Html5Qrcode("qr-reader");
      qrScannerRef.current = html5QrCode;
      
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        async (decodedText) => {
          
          const tyreTrackMatch = decodedText.match(/preventivofacilegommisti\.it\/t[iy]re\/([A-Z0-9]+)/i);
          if (tyreTrackMatch) {
            // Check if it's a TireTrack URL (supports both /tyre/ and /tire/)
            console.log("TireTrack QR detected:", decodedText);
            const tyreCode = tyreTrackMatch[1];
            await stopScanner();
            toast.success(`Pneumatico trovato: ${tyreCode}`);
            navigate(`/tyre/${tyreCode}`);
            return;
          } else if (decodedText.includes("eprel.ec.europa.eu") || decodedText.includes("/tyres/")) {
            // Check if it's an EPREL URL
            console.log("EPREL QR detected:", decodedText);
            const scanPhoto = captureFromScanner();
            await stopScanner();
            await handleEPRELScan(decodedText, scanPhoto);
          } else {
            console.log("QR UN-detected:", decodedText);
            toast.info(`QR Code rilevato: ${decodedText.substring(0, 50)}...`);
          }
        },
        () => {} // Ignore scan errors
      );
    } catch (err) {
      console.error("QR Scanner error:", err);
      setScanning(false);
      
      if (err.toString().includes("NotAllowedError") || err.toString().includes("Permission")) {
        toast.error("Permesso fotocamera negato. Abilita la fotocamera nelle impostazioni.");
      } else if (err.toString().includes("NotFoundError")) {
        toast.error("Nessuna fotocamera trovata sul dispositivo.");
      } else {
        toast.error("Errore nell'avvio dello scanner. Riprova.");
      }
      setScanMode(false);
    }
  };

  const handleScanModeToggle = async () => {
    if (scanMode) {
      // Turning off scan mode
      await stopScanner();
      setScanMode(false);
      setScannerReady(false);
    } else {
      // Turning on scan mode
      setScanMode(true);
      // scannerReady will be set by the div's onLoad/ref callback
    }
  };

  const handleEPRELScan = async (url, scanPhoto = null) => {
    setEprelLoading(true);
    try {
      console.log("EPREL URL:", url);
      // Extract product ID from EPREL URL: supports both /tyres/590129 and /qr/590129
      const productIdMatch = url.match(/\/(?:tyres|qr)\/(\d+)/);
      console.log("EPREL product match:", productIdMatch);
      if (!productIdMatch) {
        throw new Error("Impossibile estrarre l'ID prodotto dal QR code");
      }
      
      const productId = productIdMatch[1];
      // Call backend proxy to avoid browser CORS restrictions
      const response = await axios.get(`${API}/eprel/product/${productId}`);
      const eprelData = response.data;
      
      // Check if we got any useful data
      const hasData = eprelData.tyreDesignation || eprelData.additionalDetails.commercialName || eprelData.tyreSection || eprelData.aspectRatio || eprelData.rimDiameter;
      
      if (hasData) {
        toast.success("Dati EPREL importati!");
      } else {
        toast.info("QR EPREL rilevato - completa i dati manualmente");
      }

      // Check if tire already exists
      if (user?.email && hasData) {
        try {
          const fourSeasonKeywords = ["4s", "4seasons", "4 seasons", "all season", "4-season", "4 season", "4stagioni", "4 stagioni", "4-stagioni"];
          const _nomeProdotto = eprelData?.additionalDetails?.commercialName ? eprelData.additionalDetails.commercialName : eprelData.tyreDesignation;
          const commercialNameLower = (_nomeProdotto || "").toLowerCase();
          const isFourSeason = eprelData.severeSnowTyre && fourSeasonKeywords.some(k => commercialNameLower.includes(k));
          const stagione = isFourSeason ? "4 Stagioni" : eprelData.severeSnowTyre ? "Invernale" : "Estivo";

          const checkData = {
            misura: `${eprelData.tyreSection ?? ""}${eprelData.aspectRatio ?? ""}${eprelData.rimDiameter ?? ""}`,
            marca: eprelData.supplierOrTrademark || "",
            nome_prodotto: _nomeProdotto || "",
            classe_energetica: eprelData.energyClass || "",
            codice_carico: eprelData.loadCapacityIndex != null ? String(eprelData.loadCapacityIndex) : "",
            codice_velocita: eprelData.speedCategorySymbol || "",
            stato: "Nuovo",
            stagione,
            user_id: parseInt(user.UserId, 10),
            eprel_code: parseInt(productId, 10)
          };

          const checkRes = await axios.post(`${API}/tires/check-exists`, checkData);
          
          setPendingEprelData({
            eprelData,
            scanPhoto,
            eprelUrl: eprelData.raw_data?.manual_url || eprelData.raw_data?.eprel_url || url,
            eprelCode: parseInt(productId, 10)
          });
          setDuplicateTire(checkRes.data);
          setEprelLoading(false);
          return;
          
        } catch (err) {
          if (err.response?.status === 404) {
            console.log("Tire not found, proceeding to form");
          } else {
            console.error("Check-exists error:", err);
          }
        }
      }
      
      navigate("/form", { 
        state: { 
          eprelData,
          fromEPREL: true,
          photo: scanPhoto,
          eprelUrl: eprelData.raw_data?.manual_url || eprelData.raw_data?.eprel_url || url,
          eprelCode: parseInt(productId, 10)
        } 
      });
    } catch (error) {
      console.error("EPREL parsing error:", error);
      // Even on error, navigate to form with the EPREL URL for reference
      toast.info("QR rilevato - inserisci i dati manualmente");
      navigate("/form", { 
        state: { 
          eprelData: { raw_data: { eprel_url: url } },
          fromEPREL: true,
          photo: scanPhoto,
          eprelUrl: url,
          eprelCode: parseInt(productId, 10)
        } 
      });
    } finally {
      setEprelLoading(false);
    }
  };

  const capture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setPhoto(imageSrc);
      }
    }
  }, [webcamRef]);

  const retake = () => {
    setPhoto(null);
  };

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  const handleCameraError = () => {
    setCameraError(true);
    toast.error("Impossibile accedere alla fotocamera");
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Per favore seleziona un file immagine");
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setPhoto(event.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const confirmPhoto = () => {
    if (photo) {
      navigate("/form", { state: { photo } });
    }
  };

  const handleSell = async () => {
    if (!duplicateTire) return;
    
    try {
      const response = await axios.post(
        `${API}/tires/${duplicateTire.code}/sell?user_id=${parseInt(user.UserId, 10)}`,
        {
          sold_date: new Date().toISOString(),
          numero_pneumatici: soldQuantity
        }
      );
      
      if (response.data.partial_sale) {
        toast.success(`Vendute ${response.data.quantity_sold} gomme su ${response.data.quantity_sold + response.data.quantity_remaining}. Nuovo codice gomme vendute: ${response.data.sold_code}`);
      } else {
        toast.success("Pneumatico segnato come venduto");
      }
      
      setShowSellDialog(false);
      setDuplicateTire(null);
      setPendingEprelData(null);
      navigate("/");
    } catch (error) {
      toast.error("Errore nella vendita");
    }
  };

  // Callback when scanner div is mounted
  const handleScannerDivRef = useCallback((node) => {
    if (node !== null && scanMode) {
      setScannerReady(true);
    }
  }, [scanMode]);

  if (eprelLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <Loader2 className="animate-spin text-primary" size={48} />
        <p className="text-muted-foreground">Caricamento dati EPREL...</p>
      </div>
    );
  }

  return (
    <div data-testid="camera-capture" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => {
            stopScanner();
            navigate("/");
          }}
          className="h-12 w-12 p-0"
        >
          <ArrowLeft size={24} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {scanMode ? "Scansiona QR EPREL" : "Scatta Foto"}
          </h1>
          <p className="text-muted-foreground text-sm">
            {scanMode 
              ? "Inquadra il QR code dell'etichetta EPREL" 
              : "Fotografa il pneumatico o scansiona QR EPREL"}
          </p>
        </div>
      </div>

      {duplicateTire && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <Card className="w-full max-w-sm bg-card border-yellow-500/40 shadow-2xl">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <p className="font-semibold text-yellow-500 text-base">Pneumatico già esistente</p>
                <button
                  type="button"
                  onClick={() => {
                    setDuplicateTire(null);
                    setPendingEprelData(null);
                    setShowSellDialog(false);
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors ml-3 mt-0.5"
                >
                  <X size={18} />
                </button>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Esiste già un pneumatico con questi dati (codice:{" "}
                <span className="font-mono font-bold">{duplicateTire.code}</span>).
                Vuoi inserirlo come nuovo record, venderlo o modificare quello esistente?
              </p>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-10"
                    onClick={() => {
                      if (pendingEprelData) {
                        navigate("/form", {
                          state: {
                            eprelData: pendingEprelData.eprelData,
                            fromEPREL: true,
                            photo: pendingEprelData.scanPhoto,
                            eprelUrl: pendingEprelData.eprelUrl,
                            eprelCode: pendingEprelData.eprelCode
                          }
                        });
                      }
                      setDuplicateTire(null);
                      setPendingEprelData(null);
                    }}
                  >
                    Inserisci nuovo
                  </Button>
                  <Button
                    type="button"
                    className="flex-1 h-10 bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => {
                      navigate(`/tyre/${duplicateTire.code}`, { state: { editMode: true } });
                      setDuplicateTire(null);
                      setPendingEprelData(null);
                    }}
                  >
                    Modifica esistente
                  </Button>
                </div>
                <Button
                  type="button"
                  className="w-full h-10 bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => {
                    setSoldQuantity(duplicateTire.numero_pneumatici);
                    setShowSellDialog(true);
                  }}
                >
                  <ShoppingCart size={16} className="mr-2" />
                  Vendi
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sell Dialog */}
      <AlertDialog open={showSellDialog} onOpenChange={setShowSellDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Segna come venduto</AlertDialogTitle>
            <AlertDialogDescription>
              Specifica quante gomme sono state vendute.
              {duplicateTire && soldQuantity < duplicateTire.numero_pneumatici 
                ? " Le gomme rimanenti resteranno in magazzino."
                : " Questo pneumatico verrà spostato nell'archivio venduti."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="sold-quantity-modal" className="text-sm font-medium mb-2 block">
              Quantità venduta
            </Label>
            <Input
              id="sold-quantity-modal"
              type="number"
              min="1"
              max={duplicateTire?.numero_pneumatici || 1}
              value={soldQuantity}
              onChange={(e) => setSoldQuantity(parseInt(e.target.value))}
              className="w-full"
              data-testid="sold-quantity-input-modal"
            />
            {duplicateTire && (
              <p className="text-sm text-muted-foreground mt-2">
                Vendi {soldQuantity} di {duplicateTire.numero_pneumatici} gomme
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowSellDialog(false);
              setSoldQuantity(duplicateTire?.numero_pneumatici || 1);
            }}>
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-sell-btn-modal"
              onClick={handleSell}
              className="bg-blue-600 text-white hover:bg-blue-700"
              disabled={!duplicateTire || !soldQuantity || soldQuantity < 1 || soldQuantity > (duplicateTire?.numero_pneumatici || 0)}
            >
              Conferma Vendita
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          data-testid="photo-mode-btn"
          variant={!scanMode ? "default" : "secondary"}
          onClick={() => handleScanModeToggle()}
          disabled={!scanMode && scanning}
          className={`flex-1 h-12 ${!scanMode ? "bg-primary text-primary-foreground" : ""}`}
        >
          <Camera size={18} className="mr-2" />
          Foto
        </Button>
        <Button
          data-testid="qr-mode-btn"
          variant={scanMode ? "default" : "secondary"}
          onClick={() => handleScanModeToggle()}
          disabled={scanMode && !scanning}
          className={`flex-1 h-12 ${scanMode ? "bg-primary text-primary-foreground" : ""}`}
        >
          <QrCode size={18} className="mr-2" />
          Scansiona EPREL
        </Button>
      </div>

      {/* Camera / QR Scanner / Photo Preview */}
      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-0">
          {scanMode ? (
            <div className="relative bg-black" style={{ minHeight: "350px" }}>
              <div 
                id="qr-reader" 
                ref={handleScannerDivRef}
                className="w-full"
                style={{ minHeight: "350px" }}
              />
              {scanning && (
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <span className="bg-black/70 text-white px-4 py-2 rounded-full text-sm">
                    Inquadra il QR code EPREL
                  </span>
                </div>
              )}
              {!scanning && scanMode && (
                <div className="absolute inset-0 flex items-center justify-center bg-secondary">
                  <div className="text-center">
                    <Loader2 className="animate-spin text-primary mx-auto mb-2" size={32} />
                    <p className="text-muted-foreground text-sm">Avvio scanner...</p>
                  </div>
                </div>
              )}
            </div>
          ) : photo ? (
            <div className="relative">
              <img
                data-testid="photo-preview"
                src={photo}
                alt="Anteprima foto"
                className="w-full aspect-video object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          ) : cameraError ? (
            <div className="aspect-video bg-secondary flex flex-col items-center justify-center p-8">
              <Camera size={48} className="text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center mb-4">
                Fotocamera non disponibile
              </p>
              <Button
                data-testid="upload-fallback-btn"
                onClick={() => fileInputRef.current?.click()}
                className="bg-primary text-primary-foreground"
              >
                <Upload size={18} className="mr-2" />
                Carica Immagine
              </Button>
            </div>
          ) : (
            <div className="relative">
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                onUserMediaError={handleCameraError}
                className="w-full aspect-video object-cover"
              />
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-8 border-2 border-white/30 rounded-lg" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Controls */}
      {!scanMode && (
        <div className="flex justify-center gap-4">
          {photo ? (
            <>
              <Button
                data-testid="retake-btn"
                variant="secondary"
                onClick={retake}
                className="h-14 px-6 font-semibold"
              >
                <RotateCcw size={20} className="mr-2" />
                Rifai
              </Button>
              <Button
                data-testid="confirm-photo-btn"
                onClick={confirmPhoto}
                className="h-14 px-8 font-semibold bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
              >
                <Check size={20} className="mr-2" />
                Conferma
              </Button>
            </>
          ) : (
            <>
              {!cameraError && (
                <>
                  <Button
                    data-testid="toggle-camera-btn"
                    variant="secondary"
                    onClick={toggleCamera}
                    className="h-14 w-14 p-0"
                  >
                    <RotateCcw size={22} />
                  </Button>
                  <Button
                    data-testid="capture-btn"
                    onClick={capture}
                    className="h-16 w-16 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow p-0"
                  >
                    <Camera size={28} />
                  </Button>
                </>
              )}
              <Button
                data-testid="upload-btn"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                className="h-14 w-14 p-0"
              >
                <Upload size={22} />
              </Button>
            </>
          )}
        </div>
      )}

      {scanMode && (
        <div className="flex justify-center">
          <Button
            data-testid="stop-scan-btn"
            variant="secondary"
            onClick={() => handleScanModeToggle()}
            className="h-14 px-6 font-semibold"
          >
            <X size={20} className="mr-2" />
            Annulla Scansione
          </Button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
        data-testid="file-input"
      />

      {/* Instructions */}
      {!photo && !scanMode && !cameraError && (
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-2 text-sm uppercase tracking-wider text-muted-foreground">
              Suggerimenti
            </h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>Foto:</strong> Scatta una foto del pneumatico</li>
              <li>• <strong>QR EPREL:</strong> Scansiona l'etichetta energetica per auto-compilare i dati</li>
              <li>• L'etichetta EPREL si trova sull'adesivo del pneumatico nuovo</li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CameraCapture;
